<template>
  <div
    class="hero-banner container critical-component-clear"
    :class="{ 'has-slider': blocks.length > 0 }"
  >
    <splide
      class="hero-banner__carousel"
      :has-track="false"
      :options="localConfig"
    >
      <splide-track>
        <splide-slide
          v-for="(block, index) of blocks"
          :key="`hero-banner-${settings.sectionId}-${index}`"
        >
          <div
            class="hero-banner__slide"
            :class="`text-colour--${block.heading.textColour}`"
          >
            <div class="hero-banner__media">
              <template v-if="isMobile ? !block.mobileVideo : !block.desktopVideo">
                <art-direction
                  :image="block.mobileImage"
                  :image-desktop="block.desktopImage"
                  :ratio="settings.ratio"
                  :ratio-desktop="settings.ratioDesktop"
                />
              </template>

              <template v-else>
                <video-player
                  v-if="block.mobileVideo"
                  class="hero-banner__mobile-video"
                  :attributes="block.videoSettings"
                  :image="block.mobileVideo.preview_image"
                  :is-active="block.videoAutoPlay"
                  :ratio="settings.ratio"
                  sizes="100vw"
                  :sources="block.mobileVideo.sources"
                />

                <video-player
                  v-if="block.desktopVideo"
                  class="hero-banner__desktop-video"
                  :attributes="block.videoSettings"
                  :image="block.desktopVideo.preview_image"
                  :is-active="block.videoAutoPlay"
                  :ratio="settings.ratioDesktop"
                  sizes="100vw"
                  :sources="block.desktopVideo.sources"
                />
              </template>

              <div
                v-if="block.enableOverlay"
                class="hero-banner__overlay"
              />
            </div>

            <div
              class="hero-banner__content grid"
              :class="{ 'stretched-link-container': block.headingLink }"
            >
              <div :class="block.alignVertical">
                <heading-copy
                  :class="block.alignHorizontal"
                  :classes="{
                    copy: 'text-body-s-bold text-body-l-bold-desktop',
                  }"
                  :content="block.heading"
                />
              </div>
            </div>
          </div>
        </splide-slide>
      </splide-track>

      <div class="hero-banner__arrows splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-chevron-left />
        </button>

        <button
          class="splide__arrow splide__arrow--next"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-chevron-right />
        </button>
      </div>

      <ul class="hero-banner__pagination splide__pagination" />
    </splide>
  </div>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * Section featuring a full width image or video and overlayed text content.
 * - Dynamic section.
 *
 * @param {Array} blocks - Section blocks.
 * @param {String} settings.sectionId - Section's Liquid ID.
 * @param {String} settings.ratio - Image's Ratio on Mobile devices.
 * @param {String} settings.ratioDesktop - Image's Ratio on Destkop devices.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import ArtDirection from '~global/images/art-direction'
import HeadingCopy from '~async/heading-copy/heading-copy'

import IconChevronLeft from '~icons/directional-navigation/chevron/left.svg'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'HeroBanner',

  components: {
    ArtDirection,
    HeadingCopy,
    IconChevronLeft,
    IconChevronRight,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~async/video-player/video-player'
      ),
    }),
  },

  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localConfig: {},
      breakpointL: breakpoints.l,
    }
  },

  computed: {

    /**
      * Map Vuex state.
      */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  beforeMount() {
    this.localConfig = {
      arrows: true,
      direction: 'ltr',
      drag: true,
      pagination: true,
      pauseOnHover: false,
      perPage: 1,
      speed: this.$timing('normal'),
      start: 0,
      type: 'slide',
      breakpoints: {
        [breakpoints.l]: {
          arrows: false,
        },
      },
    }
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './hero-banner';
@import '../video-player/video-player';
</style>
